import { useState, useCallback, useEffect } from 'react';
import dataService from '../services/dataService';

/**
 * Hook para buscar cidades
 * @param {Object} initialParams - Parâmetros iniciais
 * @returns {Object} - Estados e funções para busca de cidades
 */
export const useCities = (initialParams = {}) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCities = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const result = await dataService.getCities({ ...initialParams, ...params });

      if (result.success) {
        setCities(result.data);
      } else {
        setError(result.message);
        setCities([]);
      }

      return result;
    } catch (err) {
      const errorMessage = 'Erro ao buscar cidades';
      setError(errorMessage);
      setCities([]);
      console.error('Erro no hook useCities:', err);
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [initialParams]);

  const searchCities = useCallback(async (searchTerm) => {
    return await fetchCities({ search: searchTerm });
  }, [fetchCities]);

  const clearCities = useCallback(() => {
    setCities([]);
    setError(null);
  }, []);

  // Busca inicial se há parâmetros
  useEffect(() => {
    if (initialParams.autoLoad !== false) {
      fetchCities();
    }
  }, []); // Executa apenas uma vez na montagem

  return {
    cities,
    loading,
    error,
    fetchCities,
    searchCities,
    clearCities,
    hasCities: cities.length > 0,
    hasError: !!error
  };
};

/**
 * Hook para buscar marcas
 * @param {Object} initialParams - Parâmetros iniciais
 * @returns {Object} - Estados e funções para busca de marcas
 */
export const useBrands = (initialParams = {}) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBrands = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const result = await dataService.getBrands({ ...initialParams, ...params });

      if (result.success) {
        setBrands(result.data);
      } else {
        setError(result.message);
        setBrands([]);
      }

      return result;
    } catch (err) {
      const errorMessage = 'Erro ao buscar marcas';
      setError(errorMessage);
      setBrands([]);
      console.error('Erro no hook useBrands:', err);
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [initialParams]);

  const searchBrands = useCallback(async (searchTerm) => {
    return await fetchBrands({ search: searchTerm });
  }, [fetchBrands]);

  const getPopularBrands = useCallback(() => {
    return brands.filter(brand => brand.popular);
  }, [brands]);

  const clearBrands = useCallback(() => {
    setBrands([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (initialParams.autoLoad !== false) {
      fetchBrands();
    }
  }, []);

  return {
    brands,
    loading,
    error,
    fetchBrands,
    searchBrands,
    getPopularBrands,
    clearBrands,
    hasBrands: brands.length > 0,
    hasError: !!error,
    popularBrands: brands.filter(brand => brand.popular)
  };
};

/**
 * Hook para buscar modelos de uma marca
 * @param {string} brandId - ID da marca
 * @param {Object} initialParams - Parâmetros iniciais
 * @returns {Object} - Estados e funções para busca de modelos
 */
export const useModels = (brandId, initialParams = {}) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchModels = useCallback(async (params = {}) => {
    if (!brandId) {
      setModels([]);
      return { success: true, data: [] };
    }

    try {
      setLoading(true);
      setError(null);

      const result = await dataService.getModelsByBrand(brandId, { ...initialParams, ...params });

      if (result.success) {
        setModels(result.data);
      } else {
        setError(result.message);
        setModels([]);
      }

      return result;
    } catch (err) {
      const errorMessage = 'Erro ao buscar modelos';
      setError(errorMessage);
      setModels([]);
      console.error('Erro no hook useModels:', err);
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [brandId, initialParams]);

  const searchModels = useCallback(async (searchTerm) => {
    return await fetchModels({ search: searchTerm });
  }, [fetchModels]);

  const getModelsByYear = useCallback((year) => {
    return models.filter(model => {
      const yearNum = parseInt(year);
      const startYear = parseInt(model.yearStart);
      const endYear = parseInt(model.yearEnd);
      return yearNum >= startYear && yearNum <= endYear;
    });
  }, [models]);

  const clearModels = useCallback(() => {
    setModels([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (brandId && initialParams.autoLoad !== false) {
      fetchModels();
    } else if (!brandId) {
      clearModels();
    }
  }, [brandId, fetchModels, clearModels]);

  return {
    models,
    loading,
    error,
    fetchModels,
    searchModels,
    getModelsByYear,
    clearModels,
    hasModels: models.length > 0,
    hasError: !!error
  };
};

/**
 * Hook para buscar produtos
 * @param {Object} initialParams - Parâmetros iniciais
 * @returns {Object} - Estados e funções para busca de produtos
 */
export const useProducts = (initialParams = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const result = await dataService.getProducts({ ...initialParams, ...params });

      if (result.success) {
        setProducts(result.data);
      } else {
        setError(result.message);
        setProducts([]);
      }

      return result;
    } catch (err) {
      const errorMessage = 'Erro ao buscar produtos';
      setError(errorMessage);
      setProducts([]);
      console.error('Erro no hook useProducts:', err);
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [initialParams]);

  const searchProducts = useCallback(async (searchTerm) => {
    return await fetchProducts({ search: searchTerm });
  }, [fetchProducts]);

  const getProductsByCategory = useCallback((category) => {
    return products.filter(product => product.category === category);
  }, [products]);

  const clearProducts = useCallback(() => {
    setProducts([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (initialParams.autoLoad !== false) {
      fetchProducts();
    }
  }, []);

  return {
    products,
    loading,
    error,
    fetchProducts,
    searchProducts,
    getProductsByCategory,
    clearProducts,
    hasProducts: products.length > 0,
    hasError: !!error
  };
};

/**
 * Hook combinado para buscar dados relacionados
 * @param {Object} params - Parâmetros iniciais
 * @returns {Object} - Todos os hooks combinados
 */
export const useVehicleData = (params = {}) => {
  const cities = useCities({ autoLoad: false, ...params.cities });
  const brands = useBrands({ autoLoad: false, ...params.brands });
  const [selectedBrand, setSelectedBrand] = useState(null);
  const models = useModels(selectedBrand?.id, { autoLoad: false, ...params.models });
  const products = useProducts({ autoLoad: false, ...params.products });

  const selectBrand = useCallback((brand) => {
    setSelectedBrand(brand);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedBrand(null);
    models.clearModels();
  }, [models]);

  const loadInitialData = useCallback(async () => {
    await Promise.all([
      cities.fetchCities(),
      brands.fetchBrands(),
      products.fetchProducts()
    ]);
  }, [cities, brands, products]);

  return {
    cities,
    brands,
    models,
    products,
    selectedBrand,
    selectBrand,
    clearSelection,
    loadInitialData,
    isLoading: cities.loading || brands.loading || models.loading || products.loading,
    hasError: cities.hasError || brands.hasError || models.hasError || products.hasError
  };
};

export default {
  useCities,
  useBrands,
  useModels,
  useProducts,
  useVehicleData
};
